/**
 * One route-level access check, applied by the router BEFORE any panel
 * activates or loads.
 *
 * Why this exists: until now the gates lived in two places that deep links
 * could walk around. `switchToTab` guarded Connect / Chase / Adoption, but
 * `switchToAdminPanel` activated the admin panels and ran their loaders for
 * anyone, and the `connect-client` route reached the Connect panel via
 * `activateNav` without consulting `canSeeConnect` at all. Hidden rail icons
 * were the only UI protection, so `#/admin/users-roles` typed or shared as a
 * link opened the Users & Roles roster for a planner. Found 2026-09-28 by the
 * JRNY-012 route-catalogue matrix (stack.testing/planner).
 *
 * ☠️ This is NOT a security boundary. The production, demo, test and training
 * rulesets are permissive (OMON-250 strict cut rolled back 2026-07-08), so any
 * signed-in account can still read `_person` / `_practitionerRole` with the
 * SDK directly. What this does is stop casual exposure — a shared link, a
 * mistyped URL — and make the UI mean what it already implies. The boundary
 * is the strict re-cut; this is the door handle, not the lock.
 *
 * Kept pure (no DOM, no state) so it is unit-tested against every screen and
 * role in the route catalogue: route-guard.test.ts.
 */
import type { Route } from "./router-parse";
import {
  canSeeAccessRequests,
  canSeeAuditLog,
  canSeeConnect,
  canSeeSomaAdoption,
  canSeeUsersAndRoles,
  type AuthClaims,
} from "./admin/claims";

/** Where every refused route lands — the one screen every role may see. */
export const REFUSED_ROUTE_LANDING: Route = { kind: "panel", panel: "dashboard" };

/**
 * The route a caller who may NOT see `route` is sent to instead, or null when
 * they may. `claims === null` (signed out, or signed in with no usable role)
 * is refused from every gated screen, exactly as the predicates already say.
 */
export function routeRefusal(route: Route, claims: AuthClaims | null): Route | null {
  const allowed = routeAllowed(route, claims);
  return allowed ? null : REFUSED_ROUTE_LANDING;
}

function routeAllowed(route: Route, claims: AuthClaims | null): boolean {
  switch (route.kind) {
    case "panel":
      if (route.panel === "connect" || route.panel === "chase") return canSeeConnect(claims);
      if (route.panel === "adoption") return canSeeSomaAdoption(claims);
      return true;
    case "connect-client":
      return canSeeConnect(claims);
    case "admin":
      if (route.panel === "users-roles") return canSeeUsersAndRoles(claims);
      if (route.panel === "access-requests") return canSeeAccessRequests(claims);
      return canSeeAuditLog(claims);
    case "client":
      return true;
  }
}
